import { createTheme } from "@mui/material/styles";

// ==========================================
// CENTRAL THEME CONFIGURATION
// Tweak your app's look and feel from here!
// ==========================================

export const themeSettings = {
  // 1. BORDER RADIUS
  // Used globally for buttons, inputs, cards, etc.
  borderRadius: 12,

  // 2. COLORS
  // Customize light and dark mode colors
  colors: {
    light: {
      primary: {
        main: "#f59e0b", // Amber
        light: "#fbbf24",
        dark: "#d97706",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#d97706",
        light: "#f59e0b",
        dark: "#b45309",
      },
      background: {
        default: "#f9fafb",
        paper: "#ffffff",
      },
      text: {
        primary: "#111827",
        secondary: "#6b7280",
      },
    },
    dark: {
      primary: {
        main: "#f59e0b", // Amber
        light: "#fbbf24",
        dark: "#d97706",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#d97706",
        light: "#f59e0b",
        dark: "#b45309",
      },
      background: {
        default: "#0f172a",
        paper: "#121b29",
      },
      text: {
        primary: "#f1f5f9",
        secondary: "#94a3b8",
      },
    },
  },

  // 3. TYPOGRAPHY (Fonts)
  // Customize the font family and styles for different text elements
  typography: {
    fontFamily: "'DM Sans', 'Poppins', system-ui, sans-serif",

    // Headings (h1, h2, h3)
    headingStyle: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    // Subheadings (h4, h5, h6, subtitle1, subtitle2)
    subheadingStyle: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    // Body text (body1, body2)
    bodyStyle: {
      fontWeight: 400,
      letterSpacing: "0",
    },
  },
};

// ==========================================
// THEME GENERATOR
// This function generates the MUI theme based on the settings above.
// ==========================================
export const getAppTheme = (isDarkMode) => {
  const mode = isDarkMode ? "dark" : "light";
  const colors = themeSettings.colors[mode];
  const { borderRadius } = themeSettings;
  const { fontFamily, headingStyle, subheadingStyle, bodyStyle } =
    themeSettings.typography;

  return createTheme({
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: colors.background,
      text: colors.text,
    },
    shape: {
      borderRadius,
    },
    typography: {
      fontFamily,
      h1: { ...headingStyle, fontSize: "40px" },
      h2: { ...headingStyle, fontSize: "32px" },
      h3: { ...headingStyle, fontSize: "28px" },
      h4: { ...subheadingStyle, fontSize: "24px" },
      h5: { ...subheadingStyle, fontSize: "20px" },
      h6: { ...subheadingStyle, fontSize: "16px" },
      subtitle1: { ...subheadingStyle, fontSize: "16px" },
      subtitle2: { ...subheadingStyle, fontSize: "14px" },
      body1: { ...bodyStyle, fontSize: "16px" },
      body2: { ...bodyStyle, fontSize: "14px" },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: `${borderRadius}px`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: `${borderRadius + 4}px`, // Cards get slightly larger radius
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: `${borderRadius + 4}px`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: `${borderRadius + 4}px`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: `${borderRadius}px`,
            },
          },
        },
      },
    },
  });
};
